import {SplitLoadMaterial} from '../types'

export interface QuantityValidationResult {
  totalQuantity: number
  minCapacity: number
  maxCapacity: number
  defaultCapacity: number
  isAtMin: boolean
  isAtMax: boolean
  isBelowMin: boolean
  isAboveMax: boolean
  canAddMaterial: boolean
  canIncreaseQuantity: boolean
  canDecreaseQuantity: boolean
  warningMessage?: string
  warningMessageKey?: string
  warningMessageParams?: Record<string, number>
}

/**
 * Calculate default quantity for a new material based on current state
 * - First two materials: default truck capacity / 2 (rounded down)
 * - Further materials: max truck capacity - total of selected quantities
 */
export const calculateDefaultQuantity = (
  selectedMaterials: SplitLoadMaterial[],
  truckCapacity: {capacity: number; minCapacity: number; maxCapacity: number}
): number => {
  const {capacity: defaultCapacity, maxCapacity} = truckCapacity

  // First two materials get half of default capacity
  if (selectedMaterials.length < 2) {
    return Math.floor(defaultCapacity / 2)
  }

  // Further materials get remaining capacity
  const totalCurrentQuantity = selectedMaterials.reduce(
    (sum, material) => sum + material.capacity.quantity,
    0
  )

  return Math.max(0, maxCapacity - totalCurrentQuantity)
}

/**
 * Validate split load material quantities
 */
export const validateSplitLoadQuantities = (
  selectedMaterials: SplitLoadMaterial[]
): QuantityValidationResult => {
  // Get truck capacity from first material (all should be the same)
  const firstMaterial = selectedMaterials[0]

  if (!firstMaterial) {
    return {
      totalQuantity: 0,
      minCapacity: 0,
      maxCapacity: 0,
      defaultCapacity: 0,
      isAtMin: false,
      isAtMax: false,
      isBelowMin: false,
      isAboveMax: false,
      canAddMaterial: true,
      canIncreaseQuantity: true,
      canDecreaseQuantity: true
    }
  }

  const {capacity: defaultCapacity, minCapacity, maxCapacity} = firstMaterial.truckCapacity

  // Calculate total quantity
  const totalQuantity = selectedMaterials.reduce(
    (sum, material) => sum + material.capacity.quantity,
    0
  )

  // Check thresholds
  const isBelowMin = totalQuantity < minCapacity
  const isAtMin = totalQuantity === minCapacity
  const isAtMax = totalQuantity === maxCapacity
  const isAboveMax = totalQuantity > maxCapacity

  // Determine what actions are allowed
  const canAddMaterial = totalQuantity < maxCapacity
  const canIncreaseQuantity = totalQuantity < maxCapacity
  const canDecreaseQuantity = totalQuantity > minCapacity

  // Generate warning message - only show warning if below min, not at min
  let warningMessage: string | undefined
  let warningMessageKey: string | undefined
  let warningMessageParams: Record<string, number> | undefined

  if (isAboveMax) {
    warningMessageKey = 'orderIntake.splitLoad.validation.exceedsMaxCapacity'
    warningMessageParams = {maxCapacity}
    warningMessage = `Total quantity exceeds maximum truck capacity of ${maxCapacity}t. Please reduce quantities.`
  } else if (isAtMax) {
    warningMessageKey = 'orderIntake.splitLoad.validation.maxCapacityReached'
    warningMessageParams = {maxCapacity}
    warningMessage = `Maximum truck capacity of ${maxCapacity}t reached.`
  } else if (isBelowMin) {
    warningMessageKey = 'orderIntake.splitLoad.validation.belowMinCapacity'
    warningMessageParams = {minCapacity}
    warningMessage = `Total quantity is below minimum truck capacity of ${minCapacity}t. Please add more materials or increase quantities.`
  } else if (isAtMin) {
    warningMessageKey = 'orderIntake.splitLoad.validation.minCapacityReached'
    warningMessageParams = {minCapacity}
    warningMessage = `Minimum truck capacity of ${minCapacity}t reached.`
  }

  return {
    totalQuantity,
    minCapacity,
    maxCapacity,
    defaultCapacity,
    isAtMin,
    isAtMax,
    isBelowMin,
    isAboveMax,
    canAddMaterial,
    canIncreaseQuantity,
    canDecreaseQuantity,
    warningMessage,
    warningMessageKey,
    warningMessageParams
  }
}

/**
 * Calculate maximum allowed quantity for a specific material
 * considering the total capacity constraint
 */
export const calculateMaxQuantityForMaterial = (
  materialIndex: number,
  selectedMaterials: SplitLoadMaterial[]
): number => {
  const firstMaterial = selectedMaterials[0]
  if (!firstMaterial) return 0

  const {maxCapacity} = firstMaterial.truckCapacity

  // Sum of all other materials' quantities
  const otherMaterialsTotal = selectedMaterials.reduce((sum, material, index) => {
    if (index === materialIndex) return sum
    return sum + material.capacity.quantity
  }, 0)

  return maxCapacity - otherMaterialsTotal
}

/**
 * Calculate minimum allowed quantity for a specific material
 * considering the minimum capacity constraint
 * Returns 0 if the material can be reduced to 0 without going below min total
 */
export const calculateMinQuantityForMaterial = (
  materialIndex: number,
  selectedMaterials: SplitLoadMaterial[]
): number => {
  const firstMaterial = selectedMaterials[0]
  if (!firstMaterial) return 0

  const {minCapacity} = firstMaterial.truckCapacity

  // Sum of all other materials' quantities
  const otherMaterialsTotal = selectedMaterials.reduce((sum, material, index) => {
    if (index === materialIndex) return sum
    return sum + material.capacity.quantity
  }, 0)

  // The minimum this material needs to be to reach total minCapacity
  const minRequired = minCapacity - otherMaterialsTotal

  // Can't be negative
  return Math.max(0, minRequired)
}
